import { useState } from 'react'
import { Download, ExternalLink, FileText, Music, X, ZoomIn, ZoomOut } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { UnifiedDocument } from './types/document-types'
import { parseDbTimestamp } from '../../utils/datetime'

interface DocumentPreviewProps {
  document: UnifiedDocument
  onClose: () => void
  onDownload: () => void
}

export function DocumentPreview({ document, onClose, onDownload }: DocumentPreviewProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(100)

  const isImage = document.mimeType?.startsWith('image/')
  const isPDF = document.mimeType === 'application/pdf'
  const isVideo = document.mimeType?.startsWith('video/')
  const isAudio = document.mimeType?.startsWith('audio/')
  const previewUrl = document.filePath ?? ''

  const handleLoad = () => setLoading(false)
  const handleError = () => {
    setLoading(false)
    setError('Erro ao carregar preview do documento')
  }

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200))
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50))

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const PreviewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" aria-hidden="true"></div>
          <span className="ml-3 text-muted-foreground">Carregando preview...</span>
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
          <FileText className="mb-4 size-16" aria-hidden="true" />
          <p className="text-lg font-medium mb-2">Preview não disponível</p>
          <p className="text-sm">{error}</p>
        </div>
      )
    }

    if (isImage) {
      return (
        <div className="flex justify-center overflow-auto max-h-96">
          <img
            src={previewUrl}
            alt={document.title}
            style={{ transform: `scale(${zoom / 100})` }}
            className="transition-transform duration-200 origin-top"
            onLoad={handleLoad}
            onError={handleError}
          />
        </div>
      )
    }

    if (isPDF) {
      return (
        <div className="w-full h-96">
          <iframe
            src={`${previewUrl}#view=FitH`}
            className="w-full h-full border-0"
            onLoad={handleLoad}
            onError={handleError}
            title={`Preview de ${document.title}`}
          />
        </div>
      )
    }

    if (isVideo) {
      return (
        <div className="flex justify-center">
          <video
            controls
            className="max-w-full max-h-96"
            onLoadedData={handleLoad}
            onError={handleError}
          >
            <source src={previewUrl} type={document.mimeType} />
            Seu navegador não suporta a reprodução de vídeo.
          </video>
        </div>
      )
    }

    if (isAudio) {
      return (
        <div className="flex flex-col items-center justify-center h-48">
          <div className="mb-4">
            <Music className="size-16 text-primary" aria-hidden="true" />
          </div>
          <audio
            controls
            className="w-full max-w-md"
            onLoadedData={handleLoad}
            onError={handleError}
          >
            <source src={previewUrl} type={document.mimeType} />
            Seu navegador não suporta a reprodução de áudio.
          </audio>
        </div>
      )
    }

    // Fallback para outros tipos de arquivo
    return (
      <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
        <FileText className="mb-4 size-16" aria-hidden="true" />
        <p className="text-lg font-medium mb-2">Preview não disponível</p>
        <p className="text-sm mb-4">Este tipo de arquivo não suporta preview.</p>
        <Button onClick={onDownload}>
          <Download className="size-4" />
          Baixar para visualizar
        </Button>
      </div>
    )
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent
        size="2xl"
        fullScreenOnMobile
        showCloseButton={false}
        className="flex flex-col gap-0 overflow-hidden p-0 sm:max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b p-4 shrink-0">
          <div className="flex-1 min-w-0">
            <DialogTitle className="truncate text-base sm:text-lg">
              {document.title}
            </DialogTitle>
            <DialogDescription className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:gap-x-4 sm:text-sm">
              <span className="truncate max-w-[140px] sm:max-w-none">{document.mimeType}</span>
              {document.fileSize && <span>{formatFileSize(document.fileSize)}</span>}
              {document.version && <span>v{document.version}</span>}
            </DialogDescription>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Zoom controls para imagens */}
            {isImage && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleZoomOut} aria-label="Diminuir zoom">
                      <ZoomOut className="size-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Diminuir zoom</TooltipContent>
                </Tooltip>
                <span className="text-sm text-muted-foreground min-w-12 text-center tabular-nums">{zoom}%</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleZoomIn} aria-label="Aumentar zoom">
                      <ZoomIn className="size-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Aumentar zoom</TooltipContent>
                </Tooltip>
                <Separator orientation="vertical" className="h-6" />
              </>
            )}

            {/* Download */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={onDownload} aria-label="Baixar documento">
                  <Download className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Baixar documento</TooltipContent>
            </Tooltip>

            {/* Open external (for PDFs) */}
            {isPDF && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => previewUrl && window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                    aria-label="Abrir em nova aba"
                  >
                    <ExternalLink className="size-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Abrir em nova aba</TooltipContent>
              </Tooltip>
            )}

            {/* Close */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fechar">
                  <X className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Fechar</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-auto flex-1 sm:max-h-[calc(90vh-120px)]">
          <PreviewContent />
        </div>

        {/* Footer with document info */}
        <div className="px-4 py-3 bg-muted/50 border-t shrink-0">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground">
            <div>
              {document.uploadedByName && (
                <span>Enviado por {document.uploadedByName}</span>
              )}
              {document.createdAt && (() => {
                const createdAt = parseDbTimestamp(document.createdAt);
                if (!createdAt) return null;
                return (
                  <span className="ml-2">
                    em {createdAt.toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                );
              })()}
            </div>
            {document.isConfidential && (
              <Badge variant="destructive">Confidencial</Badge>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
